import React from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";

const CATS = [
  { code: "btl", label: "BTL", desc: "Buy-to-let mortgage sourcing & calculator" },
  { code: "if", label: "Invoice Finance", desc: "Compare invoice finance facilities" },
  { code: "complex-btl", label: "Complex BTL", desc: "HMO / MUFB / Portfolio / Short term let" },
  { code: "af", label: "AF", desc: "Asset Finance" },
  { code: "bridging", label: "Bridging", desc: "Short-term funding" },
  { code: "development", label: "Development", desc: "Ground-up & heavy refurb" },
];

const isEnabled = (code) => code === "btl" || code === "if" || code === "complex-btl";

const productPath = (code) => {
  if (code === "btl") return "/mortgages/buy-to-let-mortgage-calculator";
  if (code === "if") return "/invoice-finance/invoice-finance-sourcing";
  if (code === "complex-btl") return "/mortgages/complex-buy-to-let-mortgage-calculator";
  return null;
};

/**
 * Product picker shown when a visitor chooses “I am a borrower” on the home page.
 * Mirrors the lender portal Choose a product modal — BTL & Invoice Finance live; others Coming soon.
 */
export default function BorrowerProductSelectModal({ open, onClose }) {
  const navigate = useNavigate();
  const [active, setActive] = React.useState(0);
  const itemRefs = React.useRef([]);

  React.useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => itemRefs.current[0]?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  React.useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  const go = (code) => {
    const path = productPath(code);
    if (!path) return;
    onClose?.();
    navigate(path);
  };

  const onGridKeyDown = (e) => {
    const max = CATS.length - 1;
    if (["ArrowRight", "ArrowDown"].includes(e.key)) {
      e.preventDefault();
      const next = Math.min(active + 1, max);
      setActive(next);
      itemRefs.current[next]?.focus();
    } else if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      const prev = Math.max(active - 1, 0);
      setActive(prev);
      itemRefs.current[prev]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cat = CATS[active];
      if (isEnabled(cat.code)) go(cat.code);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[10050] flex items-center justify-center p-3 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="borrower-product-select-title"
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} aria-hidden />

      <div className="relative z-10 w-full max-w-[22rem] sm:max-w-[32rem] md:max-w-[40rem] max-h-[min(78dvh,560px)] sm:max-h-[min(85vh,640px)] overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col">
        <div className="flex items-start justify-between gap-2 px-3.5 pt-3.5 pb-2.5 sm:p-5 shrink-0">
          <div className="min-w-0 pr-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
              Source products
            </div>
            <h3
              id="borrower-product-select-title"
              className="text-lg sm:text-xl font-extrabold text-slate-900 mt-0.5 leading-tight"
            >
              Choose a product
            </h3>
            <p className="text-slate-600 mt-1 text-xs sm:text-sm leading-snug">
              <span className="sm:hidden">BTL and Invoice Finance are live. Others coming soon.</span>
              <span className="hidden sm:inline">
                Pick a product to start sourcing. BTL and Invoice Finance are live — more products coming soon.
              </span>
            </p>
          </div>
          <button
            type="button"
            aria-label="Close"
            className="shrink-0 inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto overflow-x-hidden px-3.5 pt-1 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-5 sm:pt-1.5 sm:pb-5"
          role="listbox"
          aria-label="Product categories"
          onKeyDown={onGridKeyDown}
        >
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-2.5 p-0.5">
            {CATS.map((c, idx) => {
              const enabled = isEnabled(c.code);
              const initials =
                c.label.replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase() ||
                c.label.slice(0, 2).toUpperCase();
              return (
                <button
                  key={c.code}
                  ref={(el) => {
                    itemRefs.current[idx] = el;
                  }}
                  type="button"
                  role="option"
                  aria-selected={active === idx}
                  aria-disabled={!enabled}
                  disabled={!enabled}
                  onClick={() => go(c.code)}
                  onFocus={() => setActive(idx)}
                  className={
                    "group relative text-left p-2.5 sm:p-3.5 border rounded-xl sm:rounded-2xl bg-white transition " +
                    (enabled
                      ? "hover:bg-sky-50 hover:border-sky-300 hover:shadow-sm focus-visible:outline-none focus-visible:border-sky-400 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400"
                      : "opacity-60 cursor-not-allowed")
                  }
                >
                  {!enabled ? (
                    <span className="absolute top-1.5 right-1.5 text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-600 font-semibold leading-none">
                      Soon
                    </span>
                  ) : null}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                    <div
                      className={
                        "shrink-0 h-9 w-9 sm:h-10 sm:w-10 rounded-lg sm:rounded-xl font-extrabold grid place-items-center text-xs sm:text-sm transition " +
                        (enabled
                          ? "bg-slate-100 text-slate-700 group-hover:bg-sky-100 group-hover:text-sky-700"
                          : "bg-slate-100 text-slate-400")
                      }
                    >
                      {initials}
                    </div>
                    <div className="flex-1 min-w-0 w-full">
                      <div
                        className={
                          "font-semibold text-[13px] sm:text-sm leading-tight pr-8 sm:pr-0 " +
                          (enabled ? "text-slate-900 group-hover:text-sky-800" : "text-slate-500")
                        }
                      >
                        {c.label}
                      </div>
                      <div className="text-[10px] sm:text-xs text-slate-500 mt-0.5 line-clamp-2 leading-snug">
                        {c.desc}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
